import { useEffect, useMemo, useRef, useState } from 'react';
import { useSession, onSessionExpired } from '../../modules/session';
import { useWallet } from '../../modules/wallet';
import { LoadingScreen, ExpiredScreen } from '../../modules/shell';
import { emit } from '../../modules/postMessage';
import { createApiClient } from '../../modules/api';
import { saveOperatorCurrency } from '../../modules/currency';
import { config } from '../../config';
import { ShippingPage } from './ShippingPage';
import { ShippingTabsPage } from './ShippingTabsPage';

type Status = 'loading' | 'expired' | 'ready';

/**
 * `?with_navigation` (any value except `false`) opts the standalone route into
 * the full My Packs tab hub — Packs / Cards / Shipping with a top tab switcher.
 * Without it the route stays the single-purpose deliveries list. Read once at
 * mount from the URL, mirroring how the route reads its session token.
 */
function readWithNavigation(): boolean {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  return params.has('with_navigation') && params.get('with_navigation') !== 'false';
}

/**
 * Init / auth gate for the standalone `/shipping` route — the same session
 * handshake `App`'s `Inner` runs for the game: validate the token via the
 * WalletProvider's first balance fetch (a 401 surfaces as `wallet.error`),
 * emit `session_ready` / `session_expired`, then render the page. Lives here
 * rather than in `App` so the route stays self-contained.
 */
export function ShippingInner() {
  const session = useSession();
  // Stable identity from the provider — destructured so the effects below
  // depend on the callback itself, not on the whole session object.
  const { dropToken } = session;
  const wallet = useWallet();
  const [status, setStatus] = useState<Status>('loading');
  const didInit = useRef(false);

  const client = useMemo(
    () => createApiClient({ baseUrl: config.apiUrl, getToken: () => session.token }),
    [session.token],
  );

  // URL-driven and fixed for the page's lifetime — the parent picks the mode
  // when it opens the iframe, so a single mount-time read is enough.
  const withNavigation = useMemo(readWithNavigation, []);

  useEffect(() => {
    if (didInit.current) return;

    if (!session.token) {
      didInit.current = true;
      emit('session_expired');
      setStatus('expired');
      return;
    }

    // Wait for WalletProvider's first balance fetch to settle before routing.
    if (wallet.loading) return;

    didInit.current = true;

    if (wallet.error) {
      // Most likely a 401 / invalid token — treat as an expired session, but
      // do NOT drop it: a network failure surfaces as the same `wallet.error`
      // string, and clearing here would kill a valid session on a blip. A real
      // 401 goes through the onSessionExpired handler below, which drops it.
      emit('session_expired');
      setStatus('expired');
      return;
    }

    // Persist the operator currency from the balance the WalletProvider just
    // fetched. The game flow gets this from the pool_offers/pool_orders
    // endpoints, which a standalone /shipping page never calls — without it
    // `formatPrice` has no currency and delivery prices render blank.
    saveOperatorCurrency(wallet.currency);

    emit('session_ready', {
      user_id: 0,
      balance_cents: wallet.balance_cents ?? 0,
    });
    setStatus('ready');
  }, [session.token, wallet.loading, wallet.error, wallet.balance_cents, wallet.currency]);

  // Mid-session expiry: a 401 from any API call triggers this once.
  useEffect(
    () =>
      onSessionExpired(() => {
        dropToken();
        emit('session_expired');
        setStatus('expired');
      }),
    [dropToken],
  );

  if (status === 'loading') return <LoadingScreen />;
  if (status === 'expired') return <ExpiredScreen />;
  return withNavigation ? (
    <ShippingTabsPage client={client} />
  ) : (
    <ShippingPage client={client} />
  );
}
